// @flow
import React from "react";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";

type $Props = {
  classes: Object,
};

const Selling = ({ classes }: $Props) => (
  <div className={classes.root}>
    <Typography type="body">
      Below you will learn some tricks of the trade when it comes to selling
      your home.
    </Typography>
    <img
      className={classes.inlineImage}
      src="https://storage.googleapis.com/elevate-images/elevate-tips-selling.jpg"
      align="right"
      alt="open concept living space"
    />
    <Typography className={classes.subHeading} type="heading4">
      Tidy up
    </Typography>
    <Typography type="body">
      If you're thinking of selling your home, keep in mind that buyers
      appreciate a clean look in the homes they view. You can increase the value
      of your home and decrease the time it takes to sell by making a few simple
      improvements.
    </Typography>
    <Typography type="body">
      Aroma is the first thing prospective buyers notice when they step inside a
      home. To eliminate odors, steam clean your carpet and wash walls and
      floors with household cleaners and disinfectants. Keep your home smelling
      fresh by burning candles or potpourri, boiling a pot of cinnamon sticks or
      putting a dab of vanilla on cold light bulbs before turning them on.
    </Typography>
    <Typography type="body">
      Nothing makes a home look newer faster than painting. Painting your walls
      and removing outdated wallpaper may be the best interior improvements you
      can make. For broader appeal, paint in neutral colors such as beige,
      white, off-white, or gray. These colors suggest newness and cleanliness
      and can brighten a dull or outdated room. If your carpet is badly worn,
      outdated or stained, consider replacing it. If your carpet is heavily
      soiled, you may want to have it professionally cleaned. Brighten the
      interior of your home by cleaning your windows and opening your curtains
      to let light in. Clean hanging light fixtures and add the highest-wattage
      bulbs allowed. Below are 20 suggestions to help you sell your home.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Make the Most of that First Impression
    </Typography>
    <Typography type="body">
      A well-manicured lawn, neatly trimmed shrubs and a clutter-free porch
      welcome prospects. So does a freshly painted - or at least freshly
      scrubbed - front door. If it's autumn, rake the leaves. If it's winter,
      shovel the walkways. The fewer obstacles between prospects and the true
      appeal of your home, the better.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Invest a Few Hours for Future Dividends
    </Typography>
    <Typography type="body">
      Here's your chance to clean up in real estate. Clean up in the living
      room, the bathroom, the kitchen. If your woodwork is scuffed or the paint
      is fading, consider some minor redecoration. Fresh wallpaper adds charm
      and value to your property. Prospects would rather see how great your home
      really looks than hear how great it could look, "with a little work."
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Check Faucets and Bulbs
    </Typography>
    <Typography type="body">
      Dripping water rattles the nerves, discolors sinks and suggests faulty or
      worn-out plumbing. Burned out bulbs leave prospects in the dark. Don't let
      little problems detract from what's right with your home.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Don't Shut Out a Sale
    </Typography>
    <Typography type="body">
      If cabinets or closet doors stick in your home, you can be sure they will
      also stick in a prospect's mind. Don't try to explain away sticky
      situations when you can easily plane them away. A little effort on your
      part can smooth the way toward a closing.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Think Safety
    </Typography>
    <Typography type="body">
      Homeowners learn to live with all kinds of self-set booby traps: roller
      skates on the stairs, festooned extension cords, slippery throw rugs and
      low hanging overhead lights. Make your residence as non-perilous as
      possible for uninitiated visitors.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Make Room for Space
    </Typography>
    <Typography type="body">
      Remember, potential buyers are looking for more than just comfortable
      living space. They're looking for storage space, too. Make sure your attic
      and basement are clean and free of unnecessary items.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Consider Your Closets
    </Typography>
    <Typography type="body">
      The better organized a closet, the larger it appears. Now's the time to
      box up those unwanted clothes and donate them to charity.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Make Your Bathrooms Sparkle
    </Typography>
    <Typography type="body">
      Bathrooms sell homes, so let them shine. Check and repair damaged or
      unsightly caulking in the tubs and showers. For added allure, display your
      best towels, mats and shower curtains.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Create Dream Bedrooms
    </Typography>
    <Typography type="body">
      Wake up prospects to the cozy comforts of your bedrooms. For a spacious
      look, get rid of excess furniture. Colorful bedspreads and fresh curtains
      are a must.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Open up in the Daytime
    </Typography>
    <Typography type="body">
      Let the sun shine in! Pull back your curtains and drapes so prospects can
      see how bright and cheery your home is.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Lighten up at Night
    </Typography>
    <Typography type="body">
      Turn on the excitement by turning on all your lights - both inside and
      outside - when showing your home in the evening. Lights add color and
      warmth, and make prospects feel welcome.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Avoid Crowd Scenes
    </Typography>
    <Typography type="body">
      Potential buyers often feel like intruders when they enter a home filled
      with people. Rather than giving your house the attention it deserves,
      they're likely to hurry through. Keep the company present to a minimum.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Watch Your Pets
    </Typography>
    <Typography type="body">
      Dogs and cats are great companions, but not when you're showing your home.
      Pets have a talent for getting underfoot. So do everybody a favor: Keep
      Kitty and Spot outside, or at least out of the way.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Think Volume
    </Typography>
    <Typography type="body">
      Rock-and-roll will never die. But it might kill a real estate transaction.
      When it's time to show your home, it's time to turn down the stereo or TV.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Relax
    </Typography>
    <Typography type="body">
      Be friendly, but don't try to force conversation. Prospects want to view
      your home with a minimum of distraction.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Don't Apologize
    </Typography>
    <Typography type="body">
      No matter how humble your abode, never apologize for its shortcomings. If
      a prospect volunteers a derogatory comment about your home's appearance,
      let an experienced Real Estate Agent handle the situation.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Keep a Low Profile
    </Typography>
    <Typography type="body">
      Nobody knows your home as well as you do. But a Real Estate Agent knows
      buyers - what they need and what they want. Your Real Estate Agent will
      have an easier time articulating the virtues of your home if you stay in
      the background.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Don't Turn Your Home into a Second-Hand Store
    </Typography>
    <Typography type="body">
      When prospects come to view your home, don't distract them with offers to
      sell those furnishings you no longer need. You may lose the biggest sale
      of all.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Defer to Experience
    </Typography>
    <Typography type="body">
      When prospects want to talk price, terms, or other real estate matters,
      let them speak to an expert - your Real Estate Agent.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Help Your Agent
    </Typography>
    <Typography type="body">
      Your Real Estate Agent will have an easier time selling your home if
      showings are scheduled through his or her office. Offer to keep an eye on
      the brochure box attached to your sign and make sure it is always filled
      with flyers. Try to accommodate prospective buyers when they want to see
      your home.
    </Typography>
  </div>
);

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: "20px auto",
    maxWidth: "660px",
    "& strong": {
      fontWeight: "bold",
    },
    "& p": {
      color: theme.colors.gray["700"],
      lineHeight: "1.6",
      marginBottom: theme.spacing.unit * 1.5,
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },
    },
    "& ul": {
      listStyleType: "disc",
      lineHeight: "1.6",
      color: theme.colors.gray["700"],
      margin: "20px 14px 20px 40px",
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },

      "& li": {
        marginBottom: theme.spacing.unit * 1.5,
      },
    },
  },
  subHeading: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit * 0.5,
    fontSize: "21px",

    [theme.breakpoints(800)]: {
      fontSize: "24px",
    },
  },
  inlineImage: {
    width: "100%",
    marginBottom: theme.spacing.unit * 1.5,
    display: "block",
    maxWidth: "100%",
    height: "auto",
    verticalAlign: "middle",
  },
});

export default withStyles(styles)(Selling);
